import { FC, type CSSProperties } from 'react';
import { Box, Typography } from '@mui/material';
import type { TourItem } from '../../../../../types/tour.types';
import {
  formatTourCompanyLine,
  formatTourPriceDisplay,
  tourDateLabel,
  tourDurationLabel,
  tourRouteLabel,
} from '../../../../../utils/tourSearch.utils';
import { DateIcon, MarkerPinIcon } from '../../../../../components/Icons';
import { useIsMobile } from '../../../../../hooks/useWindowWidth';
import CustomButton from '../../../../../common/CustomButton';
import styles from './DiscoverCard.module.scss';

const THUMB_EMPTY_BG = '#D4ECFF';

function thumbStyle(imageUrl: string | undefined): CSSProperties {
  const url = imageUrl?.trim();
  if (url) return { backgroundImage: `url(${url})` };
  return { backgroundColor: THUMB_EMPTY_BG };
}

export type DiscoverCardProps = {
  item: TourItem;
  onSave?: (item: TourItem) => void;
};

const DiscoverCard: FC<DiscoverCardProps> = ({ item, onSave }) => {
  const isMobile = useIsMobile();
  const title = item.name?.trim() || 'Tour';
  const routeLabel = tourRouteLabel(item);
  const dateLabel = tourDateLabel(item);
  const durationLabel = tourDurationLabel(item);
  const companyLine = formatTourCompanyLine(item);
  const priceDisplay = formatTourPriceDisplay(item);
  const dateText = [dateLabel, durationLabel].filter(Boolean).join(' • ');

  const priceBlock = priceDisplay ? (
    <Box className={styles.priceBlock}>
      <Typography
        component="span"
        className={styles.priceLabel}
      >
        from
      </Typography>
      <Typography
        component="span"
        className={styles.priceValue}
      >
        {priceDisplay}
      </Typography>
    </Box>
  ) : null;

  return (
    <Box className={styles.card}>
      <Box className={styles.summaryRow}>
        <Box
          className={styles.thumb}
          style={thumbStyle(item.image_url)}
          role="img"
          aria-label={title}
        />

        <Box className={styles.summaryBody}>
          <Box className={styles.titleRow}>
            <Typography
              variant="h9Bold"
              className={styles.title}
            >
              {title}
            </Typography>
          </Box>

          {companyLine ? (
            <Typography
              component="span"
              className={styles.companyName}
            >
              {companyLine}
            </Typography>
          ) : null}

          <Box className={styles.metaStack}>
            {routeLabel ? (
              <Box className={styles.metaRow}>
                <span className={styles.metaIcon}>
                  <MarkerPinIcon
                    size={24}
                    color="#5E6A81"
                  />
                </span>
                <Typography
                  component="span"
                  variant="h11"
                  className={styles.metaText}
                >
                  {routeLabel}
                </Typography>
              </Box>
            ) : null}

            {dateText ? (
              <Box className={styles.metaRow}>
                <span className={styles.metaIcon}>
                  <DateIcon
                    size={24}
                    color="#5E6A81"
                  />
                </span>
                <Typography
                  component="span"
                  variant="h11"
                  className={styles.metaText}
                >
                  {dateText}
                </Typography>
              </Box>
            ) : null}
          </Box>

          {isMobile ? (
            <Box className={styles.mobileSaveWrap}>
              {priceBlock}
              <CustomButton
                variant="contained"
                btnType="button"
                textVariant="h11Bold"
                onClick={() => onSave?.(item)}
                customClass={styles.saveBtn}
              >
                Save To Ideas
              </CustomButton>
            </Box>
          ) : null}
        </Box>

        {!isMobile ? (
          <Box className={styles.asideColumn}>
            {priceBlock}
            <CustomButton
              variant="contained"
              btnType="button"
              textVariant="h11Bold"
              onClick={() => onSave?.(item)}
              customClass={styles.saveBtn}
            >
              Save To Ideas
            </CustomButton>
          </Box>
        ) : null}
      </Box>
    </Box>
  );
};

export default DiscoverCard;
